import SliderData from './SliderData';
import ColorizeSlider from './ColorizeSlider';
import type { ResolutionType } from './ResolutionConverter';

interface SliderProps {
    min: number;
    max: number;
    currentValue1: number | false;
    currentValue2: number | false;
    unit: string;
    roundValues: boolean;
    dangerZoneBelowValue?: number | false;
    dangerZoneAboveValue?: number | false;
    periodMode?: boolean;
    setAllowMinMaxSymbols?: boolean;
    darkMode?: boolean;
    resolution?: ResolutionType;
    resolutionValue?: number;
    allowToExtendRange?: boolean;
    flagOnZero?: string;
    inputsAboveSlider?: boolean;
    sliderWidth?: number;
}

interface sliderTelematicsCallbacks {
    // false === infinity
    onSliderChange: (currentValue1: number | false, currentValue2: number | false) => void;
    onChangePeriodMode: (
        periodMode: boolean,
        currentValue1: number | false,
        currentValue2: number | false
    ) => void;
}

export class sliderTelematicsRange {
    private root: HTMLDivElement;
    private props: SliderProps;
    private callbacks: sliderTelematicsCallbacks;
    private colorizeSlider: ColorizeSlider | null;
    private sliderData: SliderData | null;

    constructor(root: HTMLDivElement, props: SliderProps, callbacks: sliderTelematicsCallbacks) {
        this.root = root;
        this.props = props;
        this.callbacks = callbacks;
        this.colorizeSlider = null;
        this.sliderData = null;
        this.init();
    }

    init(appendChild: boolean = true) {
        let color_selected = '#a4d86a';
        let color_unselected = '#e6e6e6';
        let min = this.props.min;
        let max = this.props.max;
        let currentValue1 = this.props.currentValue1 === false ? min : this.props.currentValue1;
        // Math.max((this.props.currentValue1 === false ? min : this.props.currentValue1), min);
        let currentValue2 = this.props.currentValue2 === false ? max : this.props.currentValue2;
        // Math.min((this.props.currentValue2 === false ? max : this.props.currentValue2), max);
        // console.log('allowToExtendRange', this.props.allowToExtendRange, min,max, currentValue1, currentValue2);
        if (!this.props.allowToExtendRange) {
            currentValue1 = Math.max(currentValue1, min);
            currentValue2 = Math.min(currentValue2, max);
            if (currentValue1 < min) {
                currentValue1 = min;
            }
            if (currentValue2 > max) {
                currentValue2 = max;
            }
        }
        this.sliderData = new SliderData()
            .setSliderWidth(this.props.sliderWidth ?? 400)
            .setSliderHeight(15)
            .setMinimum(min)
            .setUnit(this.props.unit)
            .setVisibleMinMax(min, max)
            .allowOutsideExtremumValues(false)
            .setAllowMinMaxSymbols(this.props.setAllowMinMaxSymbols ?? true)
            .setRoundValues(this.props.roundValues);

        // console.log('allowExtendRange, data.blocks1 ', this.sliderData?.data?.blocks.map(b => b.max));

        this.sliderData.addBlock(currentValue1, color_unselected);
        this.sliderData.addBlock(currentValue2, color_selected);

        // console.log('allowExtendRange, data.blocks2 ', this.sliderData?.data?.blocks.map(b => b.max));

        if (this.props.currentValue1 === false) {
            this.sliderData.setInfinityOnBlock(0, 'from');
        }
        if (this.props.currentValue2 === false) {
            this.sliderData.setInfinityOnBlock(1, 'to');
        }

        // console.log('allowExtendRange, data.blocks3 ', this.sliderData?.data?.blocks.map(b => b.max));

        this.colorizeSlider = new ColorizeSlider(
            this.sliderData,
            {
                enableAddRemovePeriods: false,
                enableColorPicker: false,
                darkMode: this.props.darkMode ?? false,
                sliderMode: 'range',
                smallInputs: false,
                inputsAboveSlider: this.props.inputsAboveSlider ?? false,
                // valueSuffix: this.props.unit,
                dangerZoneBelowValue: this.props.dangerZoneBelowValue ?? false,
                dangerZoneAboveValue: this.props.dangerZoneAboveValue ?? false,
                enableSmartRounding: true,
                periodMode: this.props.periodMode ?? false,
                resolution: this.props.resolution ?? 'number',
                resolutionValue: this.props.resolutionValue ?? 1,
                flagOnZero: this.props.flagOnZero ?? '',
            },
            {
                onSliderChange: (sliderData: SliderData) => {
                    console.log('updateDraggableElement');
                    sliderData.setBlockColor(sliderData.getBlocks()[0].id, color_unselected);
                    sliderData.setBlockColor(sliderData.getBlocks()[1].id, color_selected);
                    this.callbacks.onSliderChange(
                        sliderData.getInfinityFrom() ? false : sliderData.getBlocks()[0].max,
                        sliderData.getInfinityTo() ? false : sliderData.getBlocks()[1].max
                    );
                },
                onSliderDragMove: (sliderData: SliderData) => {
                    sliderData.setBlockColor(sliderData.getBlocks()[0].id, color_unselected);
                    sliderData.setBlockColor(sliderData.getBlocks()[1].id, color_selected);
                    this.colorizeSlider.reloadSVGBackground();
                },
                onChangePeriodMode: periodMode => {
                    let currentValue1 = this.sliderData.getInfinityFrom()
                        ? false
                        : this.sliderData.getBlocks()[0].max;
                    let currentValue2 = this.sliderData.getInfinityTo()
                        ? false
                        : this.sliderData.getBlocks()[1].max;
                    this.callbacks.onChangePeriodMode(periodMode, currentValue1, currentValue2);
                },
            }
        );

        // this.root.innerHTML = '';
        if (appendChild) {
            this.root.innerHTML = '';
            this.root.appendChild(this.colorizeSlider.getSlider());
        } else {
            this.colorizeSlider.reload();
        }
    }

    getSlider(): ColorizeSlider | null {
        return this.colorizeSlider;
    }

    setPeriodMode(periodMode: boolean) {
        this.colorizeSlider.setPeriodMode(periodMode);
    }

    getPeriodMode(): boolean {
        return this.colorizeSlider.getPeriodMode();
    }

    getSliderData(): SliderData | null {
        return this.sliderData;
    }

    setUnit(unit: string) {
        this.sliderData.setUnit(unit);
    }

    getUnit(): string {
        return this.sliderData.getUnit();
    }

    updateSettings(props: SliderProps) {
        this.props = props;
        this.colorizeSlider = null;
        this.sliderData = null;
        this.init(true);
    }
}
